import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { CommonModule, DatePipe } from "@angular/common";
import { MatMenuModule } from "@angular/material/menu";
import { MatDialog } from "@angular/material/dialog";
import { InnerComponent } from "../message-editor/message-editor.component";
import { InternalCommunicationMessageType } from "src/app/common/enums/internal-communication-message-type.enum";
import { LandingClientService } from "src/app/common/services/landing-page.client";
import { StorageService } from "src/app/common/services/storage.service";
import { InternalCommunicationMessage } from "src/app/common/interfaces/home/internal-communication-message.interface";
import {
  ButtonComponent,
  ButtonVariant,
} from "src/app/common/components/core/button/button.component";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { ViewTypeService } from "src/app/services/view-type.service";
import { ViewType } from "src/app/common/enums/view-type.enum";
import { distinctUntilChanged, Subscription } from "rxjs";
import { formatInTimeZone } from "date-fns-tz";
import { format, parse } from "date-fns";

@Component({
  selector: "app-internal-communication",
  standalone: true,
  imports: [CommonModule, MatMenuModule, ButtonComponent],
  templateUrl: "./internal-communication.component.html",
  styleUrls: ["./internal-communication.component.scss"],
  providers: [DatePipe],
})
export class InternalCommunicationComponent implements OnInit {
  private viewTypeSubscription!: Subscription;
  private facilitySubscription?: Subscription;
  formGroup: FormGroup;
  messages: InternalCommunicationMessage[] = [];
  draft?: InternalCommunicationMessage;
  buttonVariant = ButtonVariant;
  selectedViewType: ViewType = ViewType.DOR_VIEW;
  ViewType = ViewType;
  user!: any;
  messageTypeOptions = [
    { name: "General", id: InternalCommunicationMessageType.General },
    { name: "Priority", id: InternalCommunicationMessageType.Priority },
  ];

  constructor(
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private landingClientService: LandingClientService,
    private storageService: StorageService,
    private viewTypeService: ViewTypeService,
    private datePipe: DatePipe
  ) {
    this.formGroup = new FormGroup({
      title: new FormControl("", [Validators.required]),
      message: new FormControl("", [Validators.required]),
      messageTypeControl: new FormControl(
        InternalCommunicationMessageType.General,
        [Validators.required]
      ),
    });
  }

  ngOnInit(): void {
    this.user = JSON.parse(this.storageService.getUser() ?? "");
    this.viewTypeSubscription =
      this.viewTypeService.selectedViewType$.subscribe((viewType) => {
        this.selectedViewType = viewType;
      });

    this.landingClientService
      .getJobTitleDetails()
      .subscribe((userTitleDetails) => {
        if (
          (userTitleDetails &&
            userTitleDetails.isDOR &&
            this.viewTypeService.getSelectedViewType() !==
              ViewType.THERAPIST_VIEW) ||
          (this.user.issuperuser &&
            this.viewTypeService.getSelectedViewType() !==
              ViewType.THERAPIST_VIEW)
        ) {
          this.selectedViewType = ViewType.DOR_VIEW;
          this.viewTypeService.setSelectedViewType(ViewType.DOR_VIEW);
        } else if(this.selectedViewType !== ViewType.THERAPIST_VIEW) {
          this.selectedViewType = ViewType.THERAPIST_VIEW;
          this.viewTypeService.setSelectedViewType(ViewType.THERAPIST_VIEW);
        }
      });
    const facilityId = this.storageService.getSelectedFacilityId();
    if (facilityId) {
      this.loadMessages(facilityId);
    }
    this.facilitySubscription = this.storageService.isSelectedFacility
      .pipe(distinctUntilChanged())
      .subscribe((facilityId) => {
        this.loadMessages(facilityId);
      });
  }

  ngOnDestroy(): void {
    if (this.viewTypeSubscription) {
      this.viewTypeSubscription.unsubscribe();
    }
    this.facilitySubscription?.unsubscribe();
  }

  loadMessages(facilityId: string): void {
    this.landingClientService
      .getMessages(facilityId)
      .subscribe((messages: InternalCommunicationMessage[]) => {
        this.messages = messages.filter(
          (m: InternalCommunicationMessage) => !m.savedAsDraft
        );
        this.draft = messages.find(
          (m: InternalCommunicationMessage) => m.savedAsDraft
        );
        const userMessages = messages?.filter(
          (x) => x.author === this.user.fullname
        );
        userMessages?.forEach(
          (message: InternalCommunicationMessage, index: number) => {
            message.canEdit = index === 0;
            message.canDelete = message.author === this.user.fullname;
          }
        );
        this.messages?.forEach((message) => {
          message.date = new Date(message.date + "Z");
          const timezone =
            this.storageService
              .getFacilities()
              .find((facility) => facility.facilityId === facilityId)
              ?.timezone || "US/Eastern";
          message.date = new Date(
            formatInTimeZone(
              new Date(message.date),
              timezone,
              "yyyy-MM-dd HH:mm:ss"
            )
          );
          message.date = message.date.toLocaleString();
          if (message.date) {
            try {
              const parsedDate = parse(
                message.date,
                "dd/MM/yyyy, hh:mm:ss a",
                new Date()
              );
              message.date = format(parsedDate, "MM/dd/yyyy h:mm a");
            } catch (error) {
              message.date =
                this.datePipe.transform(message.date, "MM/dd/yyyy hh:mm a") ??
                "-";
            }
          } else {
            message.date = "-";
          }
        });
        this.cdr.markForCheck();
      });
  }

  onMessageTypeSelected(selectedOption: any): void {
    const selectedMessageType = selectedOption?.id;
    this.formGroup.get("messageTypeControl")?.setValue(selectedMessageType);
  }

  addMessage(existingMessage?: InternalCommunicationMessage): void {
    const draftMessage = existingMessage || this.draft;
    const dialogRef = this.dialog.open(InnerComponent, {
      width: "500px",
      data: {
        messageType: this.messageTypeOptions,
        id: draftMessage?.id || 0,
        title: draftMessage?.title || "",
        message: draftMessage?.message || "",
        messageTypeControl:
          draftMessage?.messageType || InternalCommunicationMessageType.General,
        isDraft: draftMessage?.savedAsDraft,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const newMessage: InternalCommunicationMessage = {
          id: result?.id,
          title: result.title,
          message: result.message,
          author: result.author,
          date: result.date,
          messageType: result.messageType,
          canDelete: true,
          canEdit: true,
          savedAsDraft: result.isDraft,
        };

        const facilityId = this.storageService.getSelectedFacilityId()!;

        if (newMessage.savedAsDraft) {
          this.landingClientService.saveAsDraft(newMessage).subscribe(() => {
            this.loadMessages(facilityId);
          });
        } else if (newMessage.id) {
          this.landingClientService.updateMessage(newMessage).subscribe(() => {
            this.loadMessages(facilityId);
          });
        } else {
          this.landingClientService.addMessage(newMessage).subscribe(() => {
            this.loadMessages(facilityId);
          });
        }
      }
    });
  }

  editMessage(message: InternalCommunicationMessage): void {
    this.addMessage(message);
  }

  openDeleteConfirmation(message: InternalCommunicationMessage): void {
    this.dialog
      .open(ConfirmationModalComponent, {
        data: {
          title: "Delete Message",
          message: "Are you sure to delete the message?",
          buttonText: "Confirm",
        },
      })
      .afterClosed()
      .subscribe((accepted: boolean) => {
        if (accepted) {
          this.deleteMessage(message);
        }
      });
  }

  deleteMessage(message: InternalCommunicationMessage): void {
    const facilityId = this.storageService.getSelectedFacilityId()!;
    this.landingClientService.deleteMessage(message).subscribe(() => {
      this.loadMessages(facilityId);
    });
  }
}
